/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, NG_VALIDATORS, Validator } from '@angular/forms';
import { Directive } from '@angular/core';

@Directive({
    selector: '[appIPValidator]',
    providers: [{ provide: NG_VALIDATORS, useExisting: CreditCardValidationDirective, multi: true }]
})

/**
 * @name CreditCardValidationDirective
 * @description
 * ## Validation rules:
 * ### Card name: Discover
 * - starting with 6011, length 16-19 digits
 * - starting with 644 through 649 length 16-19 digits
 * - starting with 65 length 16-19 digits.
 * ### Card name: JCB
 * - starting with 2131 or 1800, length 15 digits
 * - starting with 3528 through 3589, length 16-19 digits
 * - starting with 35, length 16 digits
 * ### Card name: Diners Club
 * - starting with 300 through 305, length 14 digits
 * - starting with 36, or 38, length 14 digits
 * ### Card name: American Express
 * - starting with 34 or 37, length 15 digits
 * ### Card name: MasterCard
 * - starting with 51 through 55, length 16 digits
 * ### Card name: Visa
 * - starting with 4, length 13 or 16 or 19 digits
 *
 * @see {@link https://en.wikipedia.org/wiki/Payment_card_number}
 */
export class CreditCardValidationDirective implements Validator {
    public validate(control: AbstractControl): { [key: string]: any } | null {
        return creditCardValidation(control);
    }
}

/**
 * Credit card validation
 * @param control
 * @returns {any}
 */
export function creditCardValidation(control: any): any {
    if (!control?.value) {
        return null;
    }

    const cardNumberString = control?.value?.toString();
    const cardSubStringFour = cardNumberString.substring(0, 4);
    if (cardSubStringFour === '6011') {
        // Discover: Starting with 6011, length 16-19 digits
        return checkCardLength(cardNumberString, [16, 17, 18, 19], 'Discover');
    } else if (cardSubStringFour === '2131' || cardSubStringFour === '1800') {
        // JCB: Starting with 2131 or 1800, length 15 digits
        return checkCardLength(cardNumberString, [15], 'JCB');
    } else if (cardSubStringFour >= '3528' && cardSubStringFour <= '3589') {
        // JCB: Starting with 3528 through 3589, length 16-19 digits
        return checkCardLength(cardNumberString, [16, 17, 18, 19], 'JCB');
    }

    const cardSubStringThree = cardNumberString.substring(0, 3);
    if (cardSubStringThree >= '300' && cardSubStringThree <= '305') {
        // Diners Club: Starting with 300 through 305, length 14 digits
        return checkCardLength(cardNumberString, [14], 'Diners Club');
    } else if (cardSubStringThree >= '644' && cardSubStringThree <= '649') {
        // Discover: Starting with 644 through 649 length 16-19 digits
        return checkCardLength(cardNumberString, [16, 17, 18, 19], 'Discover');
    }

    const cardSubStringTwo = cardNumberString.substring(0, 2);
    if (cardSubStringTwo === '34' || cardSubStringTwo === '37') {
        // American Express: Starting with 34 or 37, length 15 digits.
        return checkCardLength(cardNumberString, [15], 'American Express');

    } else if (cardSubStringTwo >= '51' && cardSubStringTwo <= '55') {
        // MasterCard: Starting with 51 through 55, length 16 digits.
        return checkCardLength(cardNumberString, [16], 'MasterCard');
    } else if (cardSubStringTwo === '36' || cardSubStringTwo === '38') {
        // Diners Club: 36, or 38, length 14 digits.
        return checkCardLength(cardNumberString, [14], 'Diners Club');
    } else if (cardSubStringTwo === '35') {
        // JCB: starting with 35, length 16 digits.
        return checkCardLength(cardNumberString, [16], 'JCB');
    } else if (cardSubStringTwo === '65') {
        // Discover: starting with 65 length 16-19 digits.
        return checkCardLength(cardNumberString, [16, 17, 18, 19], 'Discover');
    }

    const cardSubStringOne = cardNumberString.substring(0, 1);
    if (cardSubStringOne === '4') {
        // Visa: Starting with 4, length 13 or 16 or 19 digits.
        return checkCardLength(cardNumberString, [13, 16, 19], 'Visa');
    }

    return { 'creditCardValidation': 'invalid_card_number' };
}

//TODO: Do we still need this validator?
/**
 * Helper function for card number length check.
 * @param {string} cardNumberString
 * @param {any[]} includesLength
 * @param {string} type
 * @returns {any}
 */
function checkCardLength(cardNumberString: string, includesLength: any[], type: string): any {
    return includesLength.includes(cardNumberString.length)
        ? null : { 'creditCardValidation': `Invalid ${type} card number` };
}
